/**
 * The clearing's photo mode (decision 0015): the top bar and the tray give way to a
 * viewfinder, a window onto the world in paper, with the formats above it and a shutter below
 * (Daniel, 2026-10-18). The camera still turns, tilts and zooms. The shutter makes a picture of
 * what the window shows, at the screen's own resolution so the dots stay as they are, with a slim
 * band under it that names the game, and offers it to share or save.
 */
import { type Locale, type StringKey, t } from "../i18n.ts";
import { canShareImage, shareImage } from "../share.ts";
import { getJson, setJson } from "../storage.ts";
import { esc, icon, logoUrl } from "./dom.ts";
import "./photo.css";

export interface PhotoCallbacks {
  /** The world inside a rectangle of the screen in CSS pixels, drawn now; null where it cannot be. */
  capture(rect: DOMRect): HTMLCanvasElement | null;
  /** Back to arranging the clearing: Escape in the viewfinder. */
  done(): void;
  /** The shutter, or the picture offered to share, sounds in place of the button's tap. */
  sound(kind: "shutter" | "share"): void;
}

const FORMATS = {
  "1:1": [1, 1],
  "4:5": [4, 5],
  "9:16": [9, 16],
} as const;

type Format = keyof typeof FORMATS;

const FORMAT_KEY = "clearing.photo";
/** Paper round the viewfinder, in CSS pixels. */
const MARGIN = 16;
/** A narrower picture repeats its pixels up to about this width, so the dots stay sharp. */
const MIN_WIDTH = 1080;
const SITE = "mistsweeper.com";

function isFormat(value: string | null | undefined): value is Format {
  return value !== null && value !== undefined && value in FORMATS;
}

function loadFormat(): Format {
  const saved = getJson<string>(FORMAT_KEY);
  return isFormat(saved) ? saved : "4:5";
}

/** The wordmark in the ink's colour, at a size in pixels. */
function tinted(image: HTMLImageElement, w: number, h: number, ink: string): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  if (ctx) {
    ctx.drawImage(image, 0, 0, w, h);
    ctx.globalCompositeOperation = "source-in";
    ctx.fillStyle = ink;
    ctx.fillRect(0, 0, w, h);
  }
  return c;
}

export class PhotoMode {
  private format: Format = loadFormat();
  private url: string | null = null;
  private file: File | null = null;
  private busy = false;
  private logoImage: Promise<HTMLImageElement | null> | null = null;
  private readonly locale: Locale;
  private readonly callbacks: PhotoCallbacks;
  private readonly el: HTMLElement;
  private readonly frame: HTMLElement;
  private readonly top: HTMLElement;
  private readonly bottom: HTMLElement;
  private readonly note: HTMLElement;
  private readonly flashEl: HTMLElement;
  private readonly preview: HTMLElement;
  private readonly image: HTMLImageElement;
  private readonly save: HTMLAnchorElement;
  private readonly shareButton: HTMLButtonElement;
  private readonly onResize = () => this.place();

  constructor(root: HTMLElement, locale: Locale, callbacks: PhotoCallbacks) {
    this.locale = locale;
    this.callbacks = callbacks;
    const tr = (key: StringKey) => esc(this.t(key));
    this.el = document.createElement("div");
    this.el.className = "photo";
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="photo-window"></div>
      <div class="photo-top">
        <div class="photo-formats" role="group" aria-label="${tr("clearing.photo.formats")}">
          ${Object.keys(FORMATS)
            .map((f) => `<button type="button" data-format="${f}">${f}</button>`)
            .join("")}
        </div>
      </div>
      <div class="photo-flash" hidden></div>
      <div class="photo-bottom">
        <p class="kicker photo-note" hidden></p>
        <button type="button" class="photo-shutter" aria-label="${tr("clearing.photo.shutter")}"><span></span></button>
      </div>
      <div class="photo-preview" hidden>
        <div class="photo-preview-top">
          <button type="button" class="photo-again">${icon("back", 24)}<span>${tr("clearing.photo.back")}</span></button>
          <p class="kicker">${tr("clearing.photo.picture")}</p>
        </div>
        <div class="photo-image"><img alt="${tr("clearing.photo.picture")}"></div>
        <div class="photo-actions">
          <a class="photo-save" href="#">${tr("clearing.photo.save")}</a>
          <button type="button" class="photo-share primary">${tr("clearing.photo.share")}</button>
        </div>
      </div>`;
    root.append(this.el);
    const q = <T extends HTMLElement>(sel: string) => this.el.querySelector(sel) as T;
    this.frame = q(".photo-window");
    this.top = q(".photo-top");
    this.bottom = q(".photo-bottom");
    this.note = q(".photo-note");
    this.flashEl = q(".photo-flash");
    this.preview = q(".photo-preview");
    this.image = q(".photo-image img");
    this.save = q(".photo-save");
    this.shareButton = q(".photo-share");
    this.flashEl.addEventListener("animationend", () => {
      this.flashEl.hidden = true;
    });
    this.el.addEventListener("click", (e) => this.onClick(e));
  }

  get open(): boolean {
    return !this.el.hidden;
  }

  show(): void {
    this.el.hidden = false;
    this.note.hidden = true;
    this.place();
    addEventListener("resize", this.onResize);
    void this.logo();
  }

  hide(): void {
    this.closePreview();
    this.el.hidden = true;
    removeEventListener("resize", this.onResize);
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = null;
    this.file = null;
  }

  /** Escape: closes the picture, then leaves the photo mode. */
  escape(): void {
    if (!this.preview.hidden) this.closePreview();
    else this.callbacks.done();
  }

  private t(key: StringKey): string {
    return t(this.locale, key);
  }

  private onClick(e: MouseEvent): void {
    const target = (e.target as HTMLElement).closest<HTMLElement>("button");
    if (!target) return;
    const format = target.dataset.format;
    if (isFormat(format)) {
      this.format = format;
      setJson(FORMAT_KEY, format);
      this.place();
    } else if (target.classList.contains("photo-shutter")) {
      if (!this.busy) this.callbacks.sound("shutter");
      void this.shoot();
    } else if (target.classList.contains("photo-again")) {
      this.closePreview();
    } else if (target === this.shareButton && this.file) {
      this.callbacks.sound("share");
      void shareImage(this.file);
    }
  }

  /**
   * The viewfinder: as large as the format allows between the formats and the shutter, on whole
   * device pixels of the screen. The photo mode fills the room between the game's bars, so the
   * window is placed within it.
   */
  private place(): void {
    const [fw, fh] = FORMATS[this.format];
    const box = this.el.getBoundingClientRect();
    const top = this.top.getBoundingClientRect().bottom + MARGIN;
    const bottom = this.bottom.getBoundingClientRect().top - MARGIN;
    const room = Math.max(1, bottom - top);
    let w = box.width - 2 * MARGIN;
    let h = (w * fh) / fw;
    if (h > room) {
      h = room;
      w = (h * fw) / fh;
    }
    const dpr = devicePixelRatio;
    const snap = (v: number) => Math.round(v * dpr) / dpr;
    const left = snap(box.left + (box.width - w) / 2);
    const y = snap(top + (room - h) / 2);
    for (const el of [this.frame, this.flashEl]) {
      el.style.left = `${left - box.left}px`;
      el.style.top = `${y - box.top}px`;
      el.style.width = `${snap(w)}px`;
      el.style.height = `${snap(h)}px`;
    }
    for (const b of this.el.querySelectorAll<HTMLButtonElement>("[data-format]")) {
      b.setAttribute("aria-pressed", String(b.dataset.format === this.format));
    }
  }

  private async shoot(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.note.hidden = true;
    try {
      const world = this.callbacks.capture(this.frame.getBoundingClientRect());
      this.flashEl.hidden = false;
      const blob = world ? await this.compose(world) : null;
      if (blob) this.showPreview(blob);
      else {
        this.note.textContent = this.t("clearing.photo.failed");
        this.note.hidden = false;
      }
    } finally {
      this.busy = false;
    }
  }

  /** The world as captured, its pixels repeated on a narrow screen, over a band with the name. */
  private async compose(world: HTMLCanvasElement): Promise<Blob | null> {
    const css = getComputedStyle(document.documentElement);
    const paper = css.getPropertyValue("--paper").trim() || "#f2f0e8";
    const ink = css.getPropertyValue("--ink").trim() || "#000000";
    const mono = css.getPropertyValue("--font-mono").trim() || "monospace";
    const k = Math.min(4, Math.max(1, Math.round(MIN_WIDTH / world.width)));
    const w = world.width * k;
    const h = world.height * k;
    const band = Math.round(w * 0.12);
    // The interface's 2 px line, as on a screen 360 points wide.
    const line = Math.max(2, Math.round(w / 180));
    const out = document.createElement("canvas");
    out.width = w;
    out.height = h + band;
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(world, 0, 0, w, h);
    ctx.fillStyle = paper;
    ctx.fillRect(0, h, w, band);
    ctx.fillStyle = ink;
    ctx.fillRect(0, h, w, line);
    const pad = Math.round(band * 0.36);
    const middle = h + line + (band - line) / 2;
    const logo = await this.logo();
    if (logo && logo.naturalHeight > 0) {
      const lh = Math.round(band * 0.34);
      const lw = Math.round((lh * logo.naturalWidth) / logo.naturalHeight);
      ctx.drawImage(tinted(logo, lw, lh, ink), pad, Math.round(middle - lh / 2));
    }
    ctx.font = `${Math.round(band * 0.22)}px ${mono}`;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText(SITE, w - pad, middle);
    return new Promise((resolve) => out.toBlob(resolve, "image/png"));
  }

  private showPreview(blob: Blob): void {
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(blob);
    const name = `mistsweeper-${this.t("clearing.photo.file")}.png`;
    this.file = new File([blob], name, { type: "image/png" });
    this.image.src = this.url;
    this.save.href = this.url;
    this.save.download = name;
    this.shareButton.hidden = !canShareImage();
    // Without a share sheet, saving is the way to keep the picture: the main action, as Share is.
    this.save.classList.toggle("primary", this.shareButton.hidden);
    this.preview.hidden = false;
  }

  private closePreview(): void {
    this.preview.hidden = true;
  }

  private logo(): Promise<HTMLImageElement | null> {
    this.logoImage ??= new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = logoUrl("spaced");
    });
    return this.logoImage;
  }
}
